import { apiClient } from "@/common/apiClient";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export function ViewAllKPIFormArchived() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = 10;

  const { data, isLoading, error } = useQuery({
    queryKey: ["kpi-form-archived", page, search],
    queryFn: async () => {
      const endpoint = search
        ? `/api/kpi-form-archived/search?searchTerm=${search}&page=${page}&limit=${limit}`
        : `/api/kpi-form-archived?page=${page}&limit=${limit}`;
      const response = await apiClient.get(endpoint);
      return response.data;
    },
  });

  const totalPages = data ? Math.ceil((data as any).total / limit) : 0;

  return (
    <div>
      <div className="mb-4">
        <Input
          placeholder="Search by employee name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading && !data ? (
        <div>Loading...</div>
      ) : error ? (
        <div>Error loading archived forms</div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>Employee Score</TableHead>
                <TableHead>Manager Score</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data?.map((item: any) => (
                <>
                  <TableRow
                    key={item._id}
                    onClick={() =>
                      setExpandedId(expandedId === item._id ? null : item._id)
                    }
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell>
                      {item.contactFullName || item.employeeFullName}
                    </TableCell>
                    <TableCell>{item.year}</TableCell>
                    <TableCell>
                      {item.division || item.employeeKpiFormListDivision}
                    </TableCell>
                    <TableCell>
                      {item.employeeTotalScore ||
                        item.employeeKpiFormListEmployeeTotalScore}
                    </TableCell>
                    <TableCell>
                      {item.managerTotalScore ||
                        item.employeeKpiFormListManagerTotalScore}
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">
                        {expandedId === item._id ? "Hide" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedId === item._id && (
                    <TableRow>
                      <TableCell colSpan={6} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-full max-h-96 overflow-y-auto">
                          <div>
                            <h3 className="font-semibold mb-2">
                              Employee Information
                            </h3>
                            <div className="space-y-1 text-sm">
                              <div>
                                <span className="font-medium">Name:</span>{" "}
                                {item.contactFullName || item.employeeFullName}
                              </div>
                              <div>
                                <span className="font-medium">Email:</span>{" "}
                                {item.contactEmail || item.employeeEmail}
                              </div>
                              <div>
                                <span className="font-medium">Division:</span>{" "}
                                {item.division ||
                                  item.employeeKpiFormListDivision}
                              </div>
                              {item.employeeContactId && (
                                <div>
                                  <span className="font-medium">
                                    Contact ID:
                                  </span>{" "}
                                  {item.employeeContactId}
                                </div>
                              )}
                              {item.manager && (
                                <div>
                                  <span className="font-medium">Manager:</span>{" "}
                                  {item.manager}
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="font-semibold mb-2">Scores</h3>
                            <div className="space-y-1 text-sm">
                              <div>
                                <span className="font-medium">
                                  Employee Total:
                                </span>{" "}
                                {item.employeeTotalScore ||
                                  item.surveyListEmployeeTotalScore ||
                                  item.employeeKpiFormListEmployeeTotalScore}
                              </div>
                              <div>
                                <span className="font-medium">
                                  Manager Total:
                                </span>{" "}
                                {item.managerTotalScore ||
                                  item.surveyListManagerTotalScore ||
                                  item.employeeKpiFormListManagerTotalScore}
                              </div>
                              {(item.sectionName ||
                                item.surveyQuestionSectionSurveyquestionName) && (
                                <div>
                                  <span className="font-medium">Section:</span>{" "}
                                  {item.surveyQuestionSectionSurveyquestionName ||
                                    item.sectionName}{" "}
                                  ({item.sectionNumber})
                                </div>
                              )}
                            </div>
                          </div>

                          {(item.employeeKpiFormList12MonthCareerGoal ||
                            item.surveyList12MonthsCareerGoal) && (
                            <div className="md:col-span-2">
                              <h3 className="font-semibold mb-2">
                                Career Goal (12 Month)
                              </h3>
                              <p className="text-sm break-words">
                                {item.surveyList12MonthsCareerGoal ||
                                  item.employeeKpiFormList12MonthCareerGoal ||
                                  "-"}
                              </p>
                            </div>
                          )}

                          {(item.employeeKpiFormListReviewerComment ||
                            item.surveyListManagerSComment12MonthsCareerGoal) && (
                            <div className="md:col-span-2">
                              <h3 className="font-semibold mb-2">
                                Manager Comment
                              </h3>
                              <p className="text-sm break-words">
                                {item.surveyListManagerSComment12MonthsCareerGoal ||
                                  item.employeeKpiFormListReviewerComment ||
                                  "-"}
                              </p>
                            </div>
                          )}

                          <div className="md:col-span-2">
                            <h3 className="font-semibold mb-2">Q&A</h3>
                            <div className="space-y-3 text-sm">
                              {Object.keys(item)
                                .filter(
                                  (key) =>
                                    key.startsWith("q") && /q\d+/.test(key)
                                )
                                .sort((a, b) => {
                                  const numA = parseInt(a.replace("q", ""));
                                  const numB = parseInt(b.replace("q", ""));
                                  return numA - numB;
                                })
                                .map((qKey) => {
                                  const num = qKey.replace("q", "");
                                  const aKey = `a${num}`;
                                  const commentKey = `comment${num}`;
                                  const managerCommentKey = `managerComment${num}`;
                                  const employeeScoreKey = `employeeScore${num}`;
                                  const managerScoreKey = `managerScore${num}`;

                                  return (
                                    <div key={qKey} className="border-l-2 pl-3">
                                      <div
                                        className="font-medium"
                                        dangerouslySetInnerHTML={{
                                          __html: item[qKey],
                                        }}
                                      />
                                      <div className="break-words">
                                        A: {item[aKey] || "-"}
                                      </div>
                                      {item[commentKey] && (
                                        <div className="text-muted-foreground">
                                          Manager: {item[commentKey]}
                                        </div>
                                      )}
                                      {item[managerCommentKey] && (
                                        <div className="text-muted-foreground">
                                          Manager: {item[managerCommentKey]}
                                        </div>
                                      )}
                                      {(item[employeeScoreKey] ||
                                        item[managerScoreKey]) && (
                                        <div className="text-sm mt-1">
                                          {item[employeeScoreKey] && (
                                            <span>
                                              Employee Score:{" "}
                                              {item[employeeScoreKey]}
                                            </span>
                                          )}
                                          {item[employeeScoreKey] &&
                                            item[managerScoreKey] && (
                                              <span> | </span>
                                            )}
                                          {item[managerScoreKey] && (
                                            <span>
                                              Manager Score:{" "}
                                              {item[managerScoreKey]}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={
                      page === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {/* First page */}
                <PaginationItem>
                  <PaginationLink
                    onClick={() => setPage(1)}
                    isActive={page === 1}
                    className="cursor-pointer"
                  >
                    1
                  </PaginationLink>
                </PaginationItem>

                {/* Left ellipsis */}
                {page > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Pages around current */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) => p > 1 && p < totalPages && Math.abs(p - page) <= 1
                  )
                  .map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        onClick={() => setPage(p)}
                        isActive={page === p}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                {/* Right ellipsis */}
                {page < totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Last page */}
                {totalPages > 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setPage(totalPages)}
                      isActive={page === totalPages}
                      className="cursor-pointer"
                    >
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={
                      page === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
